import { CONTACT } from "../../lib/contact";
import { useT } from "../../i18n/LanguageContext";

export default function Pricing() {
    const { t } = useT();
    const tiersCopy = t("pricing.tiers");
    const tiers = tiersCopy.map((tc, i) => ({ ...tc, accent: i === 2 }));
    const care = t("pricing.care");

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            data-cta-source="pricing"
            className="bg-surface/60 py-24 md:py-32 lg:py-40 border-y border-warm"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
                    <div className="lg:col-span-7 reveal">
                        <p className="overline mb-6">{t("pricing.overline")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-ink">
                            {t("pricing.headlineA")}{" "}
                            <em className="italic font-light text-terracotta">
                                {t("pricing.headlineEm")}
                            </em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:pt-4 reveal">
                        <p className="text-base md:text-lg leading-relaxed text-secondary">
                            {t("pricing.intro")}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 lg:gap-8">
                    {tiers.map((tier, i) => (
                        <article
                            key={tier.name}
                            data-testid={`pricing-tier-${i}`}
                            className={`reveal relative p-7 md:p-8 lg:p-10 flex flex-col h-full border ${
                                tier.accent
                                    ? "bg-ink text-sand border-ink"
                                    : "bg-[var(--sand)] border-warm"
                            }`}
                        >
                            <h3
                                className={`font-serif text-2xl md:text-[1.7rem] lg:text-[2rem] tracking-tight ${
                                    tier.accent ? "text-sand" : "text-ink"
                                }`}
                            >
                                {tier.name}
                            </h3>
                            <div className="mt-3 md:mt-4 flex items-baseline gap-3 flex-wrap">
                                <span
                                    className={`font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter ${
                                        tier.accent ? "text-sand" : "text-ink"
                                    }`}
                                >
                                    {tier.price}
                                </span>
                            </div>
                            <div
                                className={`text-[0.7rem] md:text-xs uppercase tracking-[0.16em] md:tracking-[0.18em] mt-2 ${
                                    tier.accent ? "text-sand/60" : "text-secondary"
                                }`}
                            >
                                {tier.cadence}
                            </div>
                            <p
                                className={`mt-5 text-sm ${
                                    tier.accent ? "text-sand/80" : "text-secondary"
                                }`}
                            >
                                {tier.for}
                            </p>
                            <ul className="mt-7 space-y-3 flex-1">
                                {tier.includes.map((line) => (
                                    <li
                                        key={line}
                                        className={`flex gap-3 text-sm ${
                                            tier.accent ? "text-sand/85" : "text-ink/85"
                                        }`}
                                    >
                                        <span className="mt-2 inline-block w-3 h-px shrink-0 bg-terracotta" />
                                        {line}
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={CONTACT.calendlyHref}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`pricing-tier-${i}-cta`}
                                className={`mt-8 md:mt-10 inline-flex items-center justify-center px-6 py-3.5 text-sm transition-colors ${
                                    tier.accent
                                        ? "bg-terracotta text-sand hover:bg-[var(--terracotta-hover)]"
                                        : "border border-ink text-ink hover:bg-ink hover:text-sand"
                                }`}
                            >
                                {tier.cta} →
                            </a>
                        </article>
                    ))}
                </div>

                <div className="mt-16 md:mt-20 reveal border border-warm bg-[var(--sand)] p-8 md:p-10 lg:p-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <p className="overline mb-4">{t("pricing.afterLaunchOverline")}</p>
                            <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                                {t("pricing.afterLaunchTitle")}
                            </h3>
                            <p className="mt-5 text-sm text-secondary leading-relaxed">
                                {t("pricing.afterLaunchBody")}
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {care.map(([title, body]) => (
                                <div key={title} className="border-t border-warm pt-5">
                                    <div className="font-serif text-xl text-ink">{title}</div>
                                    <p className="text-sm text-secondary mt-3 leading-relaxed">
                                        {body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
